import { Component, inject } from '@angular/core';
import { BaseViewComponent } from '../../base-view.component';
import { StateChangedModel } from '../../../common/services/state.service';
import { CustomerAccountModel, SecureDataService } from '../../../common/services/securedata.sevice';
import { take } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LanguageStateService } from '../../../common/services/language-state.service';

@Component({
    selector: 'app-customer-account-setup',
    templateUrl: './customer-account-setup.component.html',
    styleUrl: './customer-account-setup.component.scss',
    standalone: false
})
export class CustomerAccountSetupComponent extends BaseViewComponent {
  #secureDataService: SecureDataService = inject(SecureDataService)
  languageService: LanguageStateService = inject(LanguageStateService)
  fb: FormBuilder = inject(FormBuilder)
  customerAccountData: CustomerAccountModel | undefined;
  lstQuestions = [];
  customerAccountForm: FormGroup | undefined;
  showCancelDialog = false;

  protected override refreshView(): void {
    this.customerAccountForm = this.fb.group({
      pass: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
      repass: [null, [Validators.required, Validators.minLength(6), Validators.maxLength(8)]],
      securityQuestion: [null, [Validators.required]],
      securityAnswer: [null, [Validators.required]],
      coupon: ['']
    }, {
      validators: [...[this.passwordMatchValidator]]
    });
  }

  protected override onSecureDataChanged(val: StateChangedModel): void {
    if (val.Type == 'CustomerAccountSetupInfo' && val.Data != undefined) {
      const rawData = val.Data as CustomerAccountModel;
      this.customerAccountData = rawData;
      this.lstQuestions = rawData.securityQuestions ?? [];

      // Build form dynamically based on requirements
      const formConfig: Record<string, any> = {};
      const formValidators: any[] = [];

      if (rawData.isSecurityPinRequired) {
        formConfig['pass'] = [null, [Validators.required, Validators.minLength(6), Validators.maxLength(8), Validators.pattern(/^\d+$/)]];
        formConfig['repass'] = [null, [Validators.required, Validators.minLength(6), Validators.maxLength(8), Validators.pattern(/^\d+$/)]];
        formValidators.push(this.passwordMatchValidator);
      }

      if (rawData.isSecurityAnswerRequired) {
        formConfig['securityQuestion'] = [null, [Validators.required]];
        formConfig['securityAnswer'] = [null, [Validators.required]];
      }

      if (rawData.isCouponCodeAvailable) {
        formConfig['coupon'] = [''];
      }

      this.customerAccountForm = this.fb.group(formConfig, {
        validators: formValidators
      });
    }
  }

  onCancel(data: unknown) {
    this.showCancelDialog = false;
    if (data && (data as { data: { success: boolean } }).data?.success) {
      this.#secureDataService.cancel('CustomerAccountSetupInfo-Cancel').pipe(take(1)).subscribe(
        {
          next: () => {
            this.navigateWaiting(true);
          },
          error: (err) => this.onCancelError('CustomerAccountSetupInfo-Cancel', err)
        })
    }
  }

  onSubmitClicked() {
    if (this.customerAccountForm?.valid) {
      const model = this.customerAccountForm?.value;
      model.confirmSecurityAnswer = model.securityAnswer;
      this.#secureDataService.submitCustomerData(model, 'CustomerAccountSetupInfo')
        .subscribe({
          next: () => {
            this.navigateWaiting();
          },
          error: (err) => {
            this.notificationService.showError(err);
          }
        });
    } else {
      this.customerAccountForm?.markAllAsTouched();
      this.scrollToInvalidControl();
      return;
    }
  }

  onCancelClick() {
    this.showCancelDialog = true;
  }

  passwordMatchValidator(control: AbstractControl) {
    const password = control.get('pass');
    const confirmPassword = control.get('repass');

    // If either control is missing, don't attempt validation
    if (!password || !confirmPassword) {
      return null;
    }

    if (password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      const currentErrors = confirmPassword.errors;
      if (currentErrors) {
        delete currentErrors['passwordMismatch'];
        confirmPassword.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
      }
      return null;
    }
  }

  onAlphanumericInput(event: any): void {
    const input = event.target;
    input.value = input.value.replace(/[^A-Za-z0-9]/g, '');
  }
}
